import { useEffect, useState, Dispatch, SetStateAction } from 'react'
import { Button } from '@mui/material'
import { FilterPanelDropdowns } from 'components/FilterPanel'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import mapKeys from 'lodash/mapKeys'
import type { DataProviderContext } from 'providers/data'
import type { SelectData } from 'components/PureSelect/types'
import './ParameterForms.css'
import type { ReportParamValue } from 'components/Mode/Mode'
import type { ParameterFormFilterValues } from './types'
import { Grid } from '@mui/material'

interface ParameterFormsProps {
  initialParams: Record<string, ReportParamValue>
  setSelectedParams: Dispatch<SetStateAction<Record<string, ReportParamValue>>>
  setReady: Dispatch<SetStateAction<boolean>>
}

export const ParameterForms = ({
  initialParams,
  setSelectedParams,
  setReady,

  // Inherited from data context
  data
}: ParameterFormsProps & DataProviderContext<SelectData>) => {
  // All filter data inc. options, set when data is received
  const [reportParams, setReportParams] = useState([])
  // variables to capture dropdowns as they're set
  const [dropdownsValues, setDropdownsValues] =
    useState<ParameterFormFilterValues>({})

  useEffect(() => {
    if (data !== undefined) {
      //@ts-ignore
      setReportParams(data)
    }
  }, [data])

  const parseParamOutputValues = (
    values: any
  ) =>
    pickBy(
      {
        ...mapValues(
          mapKeys(values.dropdownsValues, (_, key) => `${key}`),
          //@ts-ignore
          value => value.map(({ id }) => id)
        )
      },
      value => value !== null && value.length > 0
    )

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const parsedValues = parseParamOutputValues({ dropdownsValues })

    const mergedParams = {
      ...initialParams,
      ...parsedValues
    }
    setSelectedParams(mergedParams)
    setReady(false)
  }

  const handleClearFilters = () => {
    setDropdownsValues({})
    setSelectedParams(initialParams)
    // setReady(false)
  }

  // create chunks that create equal/near-equal rows of dropdowns up to 5
  const calculateChunkSizes = (
    totalItems: number,
    maxItemsPerRow: number,
  ): number[] => {
    if (totalItems === 0) {
      return []
    }

    if (totalItems <= maxItemsPerRow) {
      return [totalItems]
    }

    const numRows = Math.ceil(totalItems / maxItemsPerRow)
    const baseItemsPerFullRow = Math.floor(totalItems / numRows)
    const remainingItems = totalItems % numRows

    const chunkSizes: number[] = []
    for (let i = 0; i < numRows; i++) {
      chunkSizes.push(baseItemsPerFullRow + (i < remainingItems ? 1 : 0))
    }
    return chunkSizes
  }

  const chunkSizes = calculateChunkSizes(reportParams.length, 5) // 5 = max dropdowns per row

  // chunk reportParams array based on chunkSizes
  let currentIndex = 0
  const chunkedReportParams: Array<Array<SelectData>> = chunkSizes.map(size => {
    const chunk = reportParams.slice(currentIndex, currentIndex + size)
    currentIndex += size
    return chunk
  })

  return (
    <form
      onSubmit={handleSubmit}
    >
      <Grid container spacing={3} sx={{ paddingBottom: '1em' }}>
        <Grid item xs={12} md={10}>
          <Grid>
            {chunkedReportParams.map((rowFilters, rowIndex) => (
              <Grid item xs={12} key={rowIndex}>
                <FilterPanelDropdowns
                  //@ts-ignore
                  dropdownsValues={dropdownsValues}
                  //@ts-ignore
                  filters={rowFilters}
                  //@ts-ignore
                  setDropdownsValues={setDropdownsValues}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} md={2}>
          <Grid
            container
            direction='column'
            alignItems='right'
            justifyContent='center'
            spacing={2}
          >
            {/* Apply filters button */}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant='outlined'
                type='submit'
                sx={{
                  height: 56,
                  marginTop: 1
                }}
              >
                Apply Filters
              </Button>
            </Grid>
            {/* Clear filters button */}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant='outlined'
                onClick={handleClearFilters}
                sx={{
                  height: 56,
                  marginTop: -1
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}
